import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { getConnectionStatus, getOnboardingState, bookmarkStep, type OnboardingChannel } from '@/api/onboarding'
import { fireCelebration } from '@/lib/confetti'
import { track } from '@/lib/analytics'
import { useShowcaseAccess } from '@/showcase/access'
import { SHOWCASE_ENABLED } from '@/config/showcase'
import { getPollsFlow } from '@/onboarding/pollsFlow'

function pluralSubs(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'подписчик'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'подписчика'
  return 'подписчиков'
}

export function ConnectDonePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { channels } = useChannel()
  const showcaseAccess = useShowcaseAccess()
  const [statusChannel, setStatusChannel] = useState<OnboardingChannel | null>(null)
  const [stateChannelUuid, setStateChannelUuid] = useState<string | null>(null)
  const pollsFlow = getPollsFlow()

  useEffect(() => {
    if (!token) return
    getConnectionStatus(token)
      .then((res) => {
        if (res.channel) setStatusChannel(res.channel)
      })
      .catch(() => {})
    getOnboardingState()
      .then((s) => {
        if (s.channelUUID) setStateChannelUuid(s.channelUUID)
      })
      .catch(() => {})
  }, [token])

  useEffect(() => {
    if (pollsFlow.active) return
    queryClient.invalidateQueries({ queryKey: ['channels'] })
    fireCelebration()
  }, [pollsFlow.active, queryClient])

  // Polls-onboarding safety-net: этот экран не должен показываться в polls-flow.
  // Если всё же попали сюда, держим loader и ждём uuid для прямого редиректа
  // в wizard, не показывая celebration UI.
  useEffect(() => {
    if (!pollsFlow.active) return
    const uuid = statusChannel?.uuid || stateChannelUuid || channels[channels.length - 1]?.uuid
    if (!uuid) return
    const qs = new URLSearchParams({ channelUUID: uuid })
    if (pollsFlow.ref) qs.set('ref', pollsFlow.ref)
    navigate(`/onboarding/polls/template?${qs.toString()}`, { replace: true })
  }, [pollsFlow.active, pollsFlow.ref, statusChannel?.uuid, stateChannelUuid, channels, navigate])

  // Источники для канала: status.channel (live от MAX API на бэке для свежеподключённого
  // канала) — приоритет для memberCount/avatarUrl. /api/max/channels может возвращать 0,
  // если асинхронный sync ещё не отработал.
  const targetUuid = statusChannel?.uuid || stateChannelUuid
  const fromList = targetUuid ? channels.find((c) => c.uuid === targetUuid) : null
  const fallbackChannel = !targetUuid && channels.length > 0 ? channels[channels.length - 1] : null

  const channel: OnboardingChannel | null = (() => {
    if (statusChannel || fromList || fallbackChannel) {
      return {
        uuid: statusChannel?.uuid || fromList?.uuid || fallbackChannel?.uuid || '',
        title: statusChannel?.title || fromList?.title || fallbackChannel?.title || 'канал',
        memberCount:
          (statusChannel?.memberCount && statusChannel.memberCount > 0
            ? statusChannel.memberCount
            : fromList?.memberCount && fromList.memberCount > 0
            ? fromList.memberCount
            : fallbackChannel?.memberCount) || 0,
        avatarUrl:
          statusChannel?.avatarUrl || fromList?.iconUrl || fallbackChannel?.iconUrl || undefined,
      }
    }
    return null
  })()

  function handleNext() {
    track('channel_connected', {
      message: channel ? `канал подключён · ${channel.title}` : 'канал подключён',
    })
    if (pollsFlow.active && channel?.uuid) {
      const qs = new URLSearchParams({ channelUUID: channel.uuid })
      if (pollsFlow.ref) qs.set('ref', pollsFlow.ref)
      navigate(`/onboarding/polls/template?${qs.toString()}`)
      return
    }
    // Phase 1 Витрины: показываем развилку только whitelist'у. Остальные
    // улетают на старый ads-only путь без изменений.
    if (SHOWCASE_ENABLED && showcaseAccess) {
      bookmarkStep('branch')
      navigate('/onboarding/branch')
    } else {
      bookmarkStep('yandex-connect', 'ads')
      navigate('/onboarding/yandex-connect')
    }
  }

  if (pollsFlow.active) {
    return (
      <OnboardingShell step="connect-done">
        <div style={{ padding: 80, textAlign: 'center', color: 'var(--tg-mute, #8f9eb0)', fontSize: 14 }}>
          Канал подключён · открываем настройку опроса…
        </div>
      </OnboardingShell>
    )
  }

  return (
    <OnboardingShell
      step="connect-done"
      footer={
        <>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handleNext}>
            Дальше
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="success-wrap">
        <div className="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>

        <p className="stage-eyebrow center">
          <span className="stage-eyebrow-pill success">Подключено</span>
        </p>
        <h1 className="stage-title center">
          Канал на&nbsp;связи. <em>Слушаем&nbsp;дальше.</em>
        </h1>
        <p className="stage-sub center">
          Уже считаем подписчиков. Полная аналитика&nbsp;— охваты, ER, источники&nbsp;— станет доступна <b>через ~24 часа</b> после первой синхронизации с&nbsp;MAX.
        </p>

        {channel && (
          <div className="success-source-card">
            <div className="success-ch-avatar">
              {channel.avatarUrl ? (
                <img src={channel.avatarUrl} alt={channel.title} />
              ) : (
                channel.title.slice(0, 1).toUpperCase()
              )}
            </div>
            <div className="success-ch-meta">
              <div className="success-ch-name">{channel.title}</div>
              <div className="success-ch-subs">
                {channel.memberCount > 0 ? (
                  <>
                    <span>
                      <b>{channel.memberCount.toLocaleString('ru-RU')}</b>&nbsp;{pluralSubs(channel.memberCount)}
                    </span>
                    <span className="dot-sep">·</span>
                    <span className="muted">остальное считаем</span>
                  </>
                ) : (
                  <span className="muted">считаем подписчиков…</span>
                )}
              </div>
            </div>
            <div className="success-ch-status">подключён</div>
          </div>
        )}

        <div className="success-eta">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          Аналитика по охватам и&nbsp;вовлечённости появится <b>завтра к этому же времени</b>
        </div>
      </div>

      <style>{`
        .success-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 700px;
          margin: 32px auto 0;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .success-icon {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: var(--tg-up, #00a860);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 28px;
          box-shadow: 0 12px 32px -8px rgba(0, 168, 96, 0.5);
          animation: success-pop 0.5s cubic-bezier(0.2,0.8,0.4,1) both;
          position: relative;
        }
        .success-icon::before, .success-icon::after {
          content: "";
          position: absolute;
          inset: -8px;
          border-radius: 50%;
          border: 1px solid rgba(0, 168, 96, 0.3);
          animation: success-ring 1.6s ease-out infinite;
        }
        .success-icon::after { animation-delay: 0.4s; }
        @keyframes success-pop {
          0% { transform: scale(0.6); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes success-ring {
          0% { inset: 0; opacity: 0.6; }
          100% { inset: -20px; opacity: 0; }
        }
        .success-icon svg { width: 48px; height: 48px; }
        .success-source-card {
          margin-top: 24px;
          width: 100%;
          max-width: 460px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          padding: 18px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          text-align: left;
          box-sizing: border-box;
        }
        .success-ch-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FF8C42, #FF4000);
          color: white;
          font-weight: 700;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
        }
        .success-ch-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .success-ch-meta { flex: 1; min-width: 0; }
        .success-ch-name {
          font-weight: 700;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .success-ch-subs {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
          font-variant-numeric: tabular-nums;
        }
        .success-ch-subs b { color: var(--tg-ink, #111); font-weight: 700; }
        .success-ch-subs .dot-sep { color: var(--tg-line-3, #d0d0d8); }
        .success-ch-subs .muted { color: var(--tg-mute, #8f9eb0); }
        .success-ch-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          background: rgba(0,168,96,0.1);
          color: var(--tg-up, #00a860);
          font-weight: 700;
          font-size: 11px;
          border-radius: 999px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .success-ch-status::before {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--tg-up, #00a860);
        }
        .success-eta {
          margin-top: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 18px;
          background: var(--tg-surface, #fff);
          border: 1px dashed var(--tg-line-3, #d0d0d8);
          border-radius: 10px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          width: 100%;
          max-width: 460px;
          box-sizing: border-box;
        }
        .success-eta b { color: var(--tg-ink, #111); font-weight: 700; }
        .success-eta svg {
          width: 16px;
          height: 16px;
          color: var(--tg-mute, #8f9eb0);
        }
        @media (max-width: 720px) {
          .success-wrap { margin-top: 20px; }
          .success-icon {
            width: 84px;
            height: 84px;
            margin-bottom: 22px;
          }
          .success-icon svg { width: 40px; height: 40px; }
          .success-source-card {
            padding: 16px;
            gap: 12px;
            align-items: flex-start;
            flex-wrap: wrap;
          }
          .success-ch-avatar {
            width: 42px;
            height: 42px;
            font-size: 16px;
          }
          .success-ch-meta {
            flex: 1 1 180px;
          }
          .success-ch-name { overflow-wrap: anywhere; }
          .success-ch-status {
            margin-left: 54px;
            max-width: calc(100% - 54px);
          }
          .success-eta {
            padding: 12px 14px;
            align-items: flex-start;
          }
        }
      `}</style>
    </OnboardingShell>
  )
}
